
import FileModificationStatusChip from '@/components/projects/summary/FileModificationStatusChip.vue';
import SignatureMatchTypeChip from '@/components/projects/summary/SignatureMatchTypeChip.vue';
import FileStatusChip from '@/components/files/FileStatusChip.vue';
import MxDataTable from '@/components/global/MxDataTable.vue';
import projectService from '@/services/ProjectService';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { EvaStatus, FileStatus, IProjectFile, IProjectFileResultSummary, ProvenanceCategoryIcons } from '@/types/resource.types';
import { IPaginatedRequest, IPaginatedResult } from '@/types/services.types';
import { paths } from '@/router/route-paths';
import { Sync } from 'vuex-pathify';

@Component({
    name: 'ProjectSummaryFilesTable',
    components: {
        FileModificationStatusChip,
        FileStatusChip,
        MxDataTable,
        SignatureMatchTypeChip
    }
})
export default class ProjectSummaryFilesTable extends Vue {

    /* Props
    ============================================*/

    @Prop({type: [Number, String], required: true})
    readonly projectId: number | string;
    @Prop({type: Boolean, required: false})
    readonly clearInterval: boolean;
    @Prop({type: Boolean, required: false, default: false})
    readonly useEva: boolean;


    /* Data
    ============================================*/

    @Sync('results/projectFileIds')
    projectFileIds: number[];

    @Sync('results/projectSummaryPath')
    projectSummaryPath: string | null;

    readonly fileStatus = FileStatus;
    readonly evaStatus = EvaStatus;
    loading: boolean = false;
    result: IPaginatedResult<IProjectFileResultSummary> | null = null;
    lastRequest: IPaginatedRequest | null = null;
    filterOption: string = 'All Project Files';
    filterOptions: string[] = [
        'All Project Files',
        'Supported Files Only',
        'Non-Submitted Files Only'
    ]

    /* Computed
    ============================================*/

    get initialRequest(): IPaginatedRequest {
        return { page: 1, perPage: 15, orderBy: 'filename', desc: false };
    }

    /* Computed
    ============================================*/

    get headers(): DataTableHeader<IProjectFileResultSummary>[] {
        return this.useEva ? evaFileResultTableHeaders : fileResultTableheaders;
    }

    /* Methods
    ============================================*/

    categoryIcon(category: string): string {
        return ProvenanceCategoryIcons[category] ?? 'mdi-help-circle-outline';
    }
    
    async goToFile(file: IProjectFileResultSummary) {
        this.projectSummaryPath = window.location.pathname + window.location.search;
        this.$router.push(paths.FILE_ANALYSIS(file.projectFileId));
        try {
            const allReq: IPaginatedRequest = {
                ...(this.lastRequest ?? { page: 1, orderBy: 'filename', desc: false }),
                page: 1,
                perPage: 9999
            };
            const allResult = await projectService.getSummaryFiles(this.projectId, allReq, this.filterOption);
            this.projectFileIds = allResult.results
                .filter(f => this.fileIsComplete(f))
                .map(f => f.projectFileId);
        } catch(e) {
            this.projectFileIds = (this.result?.results ?? [])
                .filter(f => this.fileIsComplete(f))
                .map(f => f.projectFileId);
        }
    }

    fileIsComplete(file: IProjectFileResultSummary) {
        return file.status === FileStatus.ProcessingComplete;
    }

    async getResult(req: IPaginatedRequest) {
        try {
            this.loading = true;
            this.lastRequest = req;
            let result = await projectService.getSummaryFiles(this.projectId, req, this.filterOption);
            this.result = result;
        } catch(e) {
            this.$logger.logError(e);
            this.$toast.error('There was a problem loading the file list. This error has been logged for review');
        } finally {
            this.loading = false;
        }
    }

    /* Lifecycle Hooks
    ============================================*/

    beforeMount() {
        const query = this.$route.query;
        if (query.fileFilter && typeof query.fileFilter === 'string' && this.filterOptions.includes(query.fileFilter)) {
            this.filterOption = query.fileFilter;
        }
    }

    /* Watchers
    ============================================*/

    @Watch('filterOption')
    onFilterOptionChanged() {
        (this.$refs.dataTable as any)?.reloadTableData?.();
    }

}

export const fileResultTableheaders: DataTableHeader<IProjectFileResultSummary>[] = [
    {
        text: 'Filename',
        value: 'filename'
    },
    {
        text: 'Match',
        value: 'signatureMatchType',
        sortable: false
    },
    {
        text: 'Signature',
        value: 'signature2Description',
        sortable: true
    },
    {
        text: 'Brand',
        value: 'brand',
        sortable: true
    },
    {
        text: 'Model',
        value: 'model',
        sortable: true
    },
    {
        text: 'Last Generation',
        value: 'lastGeneration',
        sortable: true
    },
    {
        text: 'Structural Consistency',
        value: 'modificationStatus',
        align: 'end',
        sortable: true
    },
    {
        text: 'Status',
        value: 'status'
    },
    {
        align: 'end',
        text: '',
        value: 'actions',
        sortable: false
    }
];

export const evaFileResultTableHeaders: DataTableHeader<IProjectFileResultSummary>[] = [
    {
        text: 'Filename',
        value: 'filename'
    },
    {
        text: 'Signature',
        value: 'signature2Description',
        sortable: true
    },
    {
        text: 'Last Generation',
        value: 'lastGeneration',
        sortable: true
    },
    {
        text: 'Last Generation Category',
        value: 'lastGenerationCategory',
        sortable: true,
        align: 'center'
    },
    {
        text: 'Status',
        value: 'status'
    },
    {
        align: 'end',
        text: '',
        value: 'actions',
        sortable: false
    }
];

export const fileTableheaders: DataTableHeader<IProjectFile>[] = [
    {
        text: 'Filename',
        value: 'filename'
    },
    {
        text: 'Originating Device',
        value: 'originatingDevice',
        sortable: false
    },
    {
        text: 'Last Generation',
        value: 'lastGeneration',
        sortable: false
    },
    {
        text: 'Status',
        value: 'status'
    },
    {
        text: 'Structural Consistency Analysis',
        value: 'modificationStatus',
        align: 'end',
        sortable: false
    },
    {
        align: 'end',
        text: '',
        value: 'actions',
        sortable: false
    }
];

